import Button from 'devextreme-react/button';
import DataGrid, {
  Column,
  ColumnChooser,
  HeaderFilter,
  Item,
  Lookup,
  Paging,
  Scrolling,
  SearchPanel,
  Toolbar,
  type DataGridRef
} from 'devextreme-react/data-grid';
import DateBox from 'devextreme-react/date-box';
import NumberBox from 'devextreme-react/number-box';
import SelectBox from 'devextreme-react/select-box';
import TextArea from 'devextreme-react/text-area';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GridFormPopup, OPS_SITE_GRID_PROPS, OpsGridStateStoring, opsGridFormPopupSizeKey, onGridRowDblClick } from '../opsDataGrid';
import { PatientTrackingGridToolbarItems } from './PatientTrackingGridToolbar';
import { patientTrackingApi } from './patientTrackingApi';
import { useLiveRows } from './useLiveRows';
import {
  ALERT_RESULTS,
  PatientTrackingBoardKeys,
  type AlertResult,
  type AlertingHistoryDto,
  type AlertingHistoryUpsertBody,
  type OurPharmacyDto
} from './patientTrackingTypes';
import './patientTracking.css';

export interface AlertingHistoryGridProps {
  stateStorageKey?: string;
}

const RESULT_OPTIONS = ALERT_RESULTS.map((value) => ({ value, label: value }));

const ALERTING_HISTORY_GRID_SCHEMA_VERSION = 1;
const ALERTING_HISTORY_GRID_COLUMNS = [
  'sentDate',
  'pharmacyName',
  'result',
  'summary',
  'ceAlerted',
  'patientAlerts',
  'emailsSent',
  'errors',
  'totalRecords'
] as const;

const emptyForm = (): AlertingHistoryUpsertBody => ({
  pharmacyId: '',
  sentDate: new Date().toISOString(),
  result: 'Success',
  summary: null,
  totalRecords: 0,
  patientAlerts: 0,
  ceAlerted: 0,
  emailsSent: 0,
  errors: 0,
  sentToIds: null,
  errorIds: null
});

function toBody(h: AlertingHistoryDto): AlertingHistoryUpsertBody {
  return {
    pharmacyId: h.pharmacyId,
    sentDate: h.sentDate,
    result: h.result,
    summary: h.summary,
    totalRecords: h.totalRecords,
    patientAlerts: h.patientAlerts,
    ceAlerted: h.ceAlerted,
    emailsSent: h.emailsSent,
    errors: h.errors,
    sentToIds: h.sentToIds,
    errorIds: h.errorIds
  };
}

export function AlertingHistoryGrid({
  stateStorageKey = 'alerting-history-grid'
}: AlertingHistoryGridProps) {
  const { rows, loading, error, reload, applyUpsert } = useLiveRows<AlertingHistoryDto>(
    PatientTrackingBoardKeys.alertingHistory,
    patientTrackingApi.listAlertingHistory,
    { collaboration: false }
  );

  const gridRef = useRef<DataGridRef>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [pharmacies, setPharmacies] = useState<OurPharmacyDto[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<AlertingHistoryUpsertBody>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    patientTrackingApi.listPharmacies().then(setPharmacies).catch(() => setPharmacies([]));
  }, []);

  const pharmacyOptions = useMemo(
    () => pharmacies.map((p) => ({ id: p.id, name: p.pharmacyName })),
    [pharmacies]
  );

  const openCreate = useCallback(() => {
    setEditingId(null);
    setForm(emptyForm());
    setFormError(null);
    setEditorOpen(true);
  }, []);

  const openEdit = useCallback((row: AlertingHistoryDto) => {
    setEditingId(row.id);
    setForm(toBody(row));
    setFormError(null);
    setEditorOpen(true);
  }, []);

  const save = async () => {
    setSaving(true);
    setFormError(null);
    try {
      const body = { ...form, summary: form.summary?.trim() || null };
      if (!body.pharmacyId) throw new Error('Select a pharmacy.');
      const dto = editingId == null
        ? await patientTrackingApi.createAlertingHistory(body)
        : await patientTrackingApi.updateAlertingHistory(editingId, body);
      applyUpsert(dto);
      setEditorOpen(false);
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!editingId) return;
    if (!window.confirm('Delete this history entry?')) return;
    setSaving(true);
    try {
      await patientTrackingApi.deleteAlertingHistory(editingId);
      setEditorOpen(false);
      await reload();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Delete failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <DataGrid
        ref={gridRef}
        dataSource={rows}
        keyExpr="id"
        height="100%"
        width="100%"
        showBorders
        columnAutoWidth
        columnHidingEnabled
        allowColumnResizing
        allowColumnReordering
        columnResizingMode="widget"
        {...OPS_SITE_GRID_PROPS}
        noDataText={error ?? (loading ? 'Loading…' : 'No alerting history yet.')}
        onRowDblClick={(e) => onGridRowDblClick(e, openEdit)}
      >
        <OpsGridStateStoring
          storageKey={stateStorageKey}
          schemaVersion={ALERTING_HISTORY_GRID_SCHEMA_VERSION}
          columns={ALERTING_HISTORY_GRID_COLUMNS}
        />
        <Toolbar>
          <PatientTrackingGridToolbarItems
            gridRef={gridRef}
            loading={loading}
            onRefresh={() => void reload()}
            newItem={{ hint: 'Add history entry', onClick: openCreate }}
            export={{
              fileName: 'alerting-history',
              worksheetName: 'Alerting History',
              hint: 'Export alerting history'
            }}
          />
          <Item name="searchPanel" location="after" />
        </Toolbar>
        <SearchPanel visible width={240} placeholder="Search history…" />
        <HeaderFilter visible />
        <ColumnChooser enabled mode="select" allowSearch title="Choose columns" />
        <Scrolling mode="standard" scrollByThumb showScrollbar="onScroll" useNative />
        <Paging defaultPageSize={50} />

        <Column dataField="sentDate" caption="Sent" dataType="datetime" width={170} sortOrder="desc" />
        <Column dataField="pharmacyName" caption="Pharmacy" minWidth={160} />
        <Column dataField="result" caption="Result" width={110}>
          <Lookup dataSource={RESULT_OPTIONS} valueExpr="value" displayExpr="label" />
        </Column>
        <Column dataField="summary" caption="Summary" minWidth={260} />
        <Column dataField="ceAlerted" caption="CEs Alerted" dataType="number" width={110} />
        <Column dataField="patientAlerts" caption="Patient Alerts" dataType="number" width={120} />
        <Column dataField="emailsSent" caption="Emails Sent" dataType="number" width={110} />
        <Column dataField="errors" caption="Errors" dataType="number" width={90} />
        <Column dataField="totalRecords" caption="Total Records" dataType="number" width={120} visible={false} />
      </DataGrid>

      <GridFormPopup
        visible={editorOpen}
        title={editingId == null ? 'Add history entry' : 'Edit history entry'}
        onClose={() => setEditorOpen(false)}
        sizeStorageKey={opsGridFormPopupSizeKey(stateStorageKey)}
        error={formError}
        footer={
          <div className="patient-tracking-popup-footer">
            <div>
              {editingId != null ? (
                <Button text="Delete" type="danger" disabled={saving} onClick={() => void remove()} />
              ) : null}
            </div>
            <div className="patient-tracking-popup-footer__actions">
              <Button text="Cancel" disabled={saving} onClick={() => setEditorOpen(false)} />
              <Button text={saving ? 'Saving…' : 'Save'} type="default" disabled={saving} onClick={() => void save()} />
            </div>
          </div>
        }
      >
        <div className="patient-form-grid">
          <label>
            <span className="patient-form-label patient-form-label--required">Pharmacy</span>
            <SelectBox
              dataSource={pharmacyOptions}
              valueExpr="id"
              displayExpr="name"
              searchEnabled
              value={form.pharmacyId || null}
              placeholder="Select pharmacy…"
              onValueChanged={(e) => setForm((f) => ({ ...f, pharmacyId: (e.value as string) ?? '' }))}
            />
          </label>
          <label>
            <span className="patient-form-label">Sent date</span>
            <DateBox
              type="datetime"
              value={form.sentDate}
              onValueChanged={(e) =>
                setForm((f) => ({
                  ...f,
                  sentDate: e.value ? new Date(e.value as string | Date).toISOString() : f.sentDate
                }))
              }
            />
          </label>
          <label>
            <span className="patient-form-label">Result</span>
            <SelectBox
              dataSource={RESULT_OPTIONS}
              valueExpr="value"
              displayExpr="label"
              value={form.result}
              onValueChanged={(e) => setForm((f) => ({ ...f, result: (e.value as AlertResult) ?? 'Success' }))}
            />
          </label>
          <label>
            <span className="patient-form-label">CEs alerted</span>
            <NumberBox value={form.ceAlerted} min={0} onValueChanged={(e) => setForm((f) => ({ ...f, ceAlerted: Number(e.value ?? 0) }))} />
          </label>
          <label>
            <span className="patient-form-label">Patient alerts</span>
            <NumberBox value={form.patientAlerts} min={0} onValueChanged={(e) => setForm((f) => ({ ...f, patientAlerts: Number(e.value ?? 0) }))} />
          </label>
          <label>
            <span className="patient-form-label">Emails sent</span>
            <NumberBox value={form.emailsSent} min={0} onValueChanged={(e) => setForm((f) => ({ ...f, emailsSent: Number(e.value ?? 0) }))} />
          </label>
          <label>
            <span className="patient-form-label">Errors</span>
            <NumberBox value={form.errors} min={0} onValueChanged={(e) => setForm((f) => ({ ...f, errors: Number(e.value ?? 0) }))} />
          </label>
          <label className="patient-form-grid__full">
            <span className="patient-form-label">Summary</span>
            <TextArea
              value={form.summary ?? ''}
              className="patient-form-textarea--lg"
              autoResizeEnabled
              minHeight={72}
              onValueChanged={(e) => setForm((f) => ({ ...f, summary: e.value ?? '' }))}
            />
          </label>
        </div>
      </GridFormPopup>
    </>
  );
}
